import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { DatePicker } from '../components/ui/DatePicker';
import { InvoiceStatusBadge } from '../components/domain/StatusBadge';
import { Invoice } from '../types';
import { formatCurrency, formatDate } from '../utils/calculations';
import { PlusIcon, SearchIcon, EyeIcon, MailIcon, MessageSquareIcon, CheckCircleIcon, FilterIcon } from 'lucide-react';
export function Invoices() {
  const {
    filteredInvoices,
    getCustomerById,
    updateInvoice
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Filter invoices
  const filteredData = useMemo(() => {
    return filteredInvoices.filter(invoice => {
      // Search filter
      const customer = getCustomerById(invoice.customer_id);
      const searchMatch = !searchQuery || invoice.id.toLowerCase().includes(searchQuery.toLowerCase()) || customer?.name.toLowerCase().includes(searchQuery.toLowerCase());
      // Status filter
      const statusMatch = statusFilter === 'all' || invoice.status === statusFilter;
      // Date filter
      const invoiceDate = new Date(invoice.date);
      const startMatch = !startDate || invoiceDate >= new Date(startDate);
      const endMatch = !endDate || invoiceDate <= new Date(endDate);
      return searchMatch && statusMatch && startMatch && endMatch;
    });
  }, [filteredInvoices, searchQuery, statusFilter, startDate, endDate, getCustomerById]);
  // Sort by date descending
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [filteredData]);
  const handleMarkPaid = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      await updateInvoice(invoice.id, {
        status: 'paid'
      });
    } catch (error) {
      console.error('Failed to update invoice:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendEmail = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      await updateInvoice(invoice.id, {
        sent_via_email: true,
        status: 'sent'
      });
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendSMS = async (invoice: Invoice) => {
    setIsLoading(true);
    try {
      await updateInvoice(invoice.id, {
        sent_via_sms: true,
        status: 'sent'
      });
    } catch (error) {
      console.error('Failed to send SMS:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'refunded',
    label: 'Refunded'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  const columns = [{
    key: 'id',
    header: 'Invoice',
    render: (invoice: Invoice) => <Link to={`/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-364">
          {invoice.id.slice(0, 12)}...
        </Link>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: Invoice) => {
      const customer = getCustomerById(invoice.customer_id);
      return <span className="font-medium" data-id="element-365">{customer?.name || 'Unknown'}</span>;
    }
  }, {
    key: 'date',
    header: 'Date',
    render: (invoice: Invoice) => formatDate(invoice.date)
  }, {
    key: 'total',
    header: 'Total',
    render: (invoice: Invoice) => <span className="font-semibold" data-id="element-366">
          {formatCurrency(invoice.total_amount)}
        </span>
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: Invoice) => <InvoiceStatusBadge status={invoice.status} data-id="element-367" />
  }, {
    key: 'sent',
    header: 'Sent Via',
    render: (invoice: Invoice) => <div className="flex items-center gap-2" data-id="element-368">
          {invoice.sent_via_email && <span title="Email sent" className="text-green-600" data-id="element-369">
              <MailIcon className="w-4 h-4" data-id="element-370" />
            </span>}
          {invoice.sent_via_sms && <span title="SMS sent" className="text-green-600" data-id="element-371">
              <MessageSquareIcon className="w-4 h-4" data-id="element-372" />
            </span>}
          {!invoice.sent_via_email && !invoice.sent_via_sms && <span className="text-gray-400 text-sm" data-id="element-373">-</span>}
        </div>
  }, {
    key: 'actions',
    header: '',
    render: (invoice: Invoice) => <div className="flex items-center justify-end gap-1" data-id="element-374">
          <Link to={`/invoices/${invoice.id}`} data-id="element-375">
            <Button variant="ghost" size="sm" aria-label="View invoice" data-id="element-376">
              <EyeIcon className="w-4 h-4" data-id="element-377" />
            </Button>
          </Link>
          {invoice.status === 'draft' && <>
              <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleSendEmail(invoice);
        }} disabled={isLoading} aria-label="Send via email" data-id="element-378">
                <MailIcon className="w-4 h-4" data-id="element-379" />
              </Button>
              <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleSendSMS(invoice);
        }} disabled={isLoading} aria-label="Send via SMS" data-id="element-380">
                <MessageSquareIcon className="w-4 h-4" data-id="element-381" />
              </Button>
            </>}
          {invoice.status === 'sent' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleMarkPaid(invoice);
      }} disabled={isLoading} className="text-green-600 hover:text-green-700 hover:bg-green-50" aria-label="Mark as paid" data-id="element-382">
              <CheckCircleIcon className="w-4 h-4" data-id="element-383" />
            </Button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-384">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-385">
        <div data-id="element-386">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-387">Invoices</h1>
          <p className="text-gray-500 mt-1" data-id="element-388">
            {sortedData.length} invoice{sortedData.length !== 1 ? 's' : ''}{' '}
            found
          </p>
        </div>
        <Link to="/invoices/new" data-id="element-389">
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-391" />} data-id="element-390">
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card data-id="element-392">
        <div className="space-y-4" data-id="element-393">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-394">
            <div className="flex-1 relative" data-id="element-395">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-396" />
              <Input placeholder="Search by invoice ID or customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-397" />
            </div>
            <div className="w-full sm:w-48" data-id="element-398">
              <Select options={statusOptions} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-399" />
            </div>
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} leftIcon={<FilterIcon className="w-4 h-4" data-id="element-401" />} data-id="element-400">
              {showFilters ? 'Hide Filters' : 'More Filters'}
            </Button>
          </div>

          {showFilters && <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200" data-id="element-402">
              <div className="w-full sm:w-48" data-id="element-403">
                <DatePicker label="From Date" value={startDate} onChange={e => setStartDate(e.target.value)} data-id="element-404" />
              </div>
              <div className="w-full sm:w-48" data-id="element-405">
                <DatePicker label="To Date" value={endDate} onChange={e => setEndDate(e.target.value)} data-id="element-406" />
              </div>
              <div className="flex items-end" data-id="element-407">
                <Button variant="ghost" onClick={() => {
              setStartDate('');
              setEndDate('');
              setStatusFilter('all');
              setSearchQuery('');
            }} data-id="element-408">
                  Clear Filters
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {/* Table */}
      <Table columns={columns} data={sortedData} keyExtractor={inv => inv.id} emptyMessage="No invoices found" data-id="element-409" />
    </div>;
}